<template>
  <div class="task-board">
    <div v-for="col in columns" :key="col.status" class="task-col">
      <div class="task-col__head" :class="`task-col__head--${col.status.toLowerCase()}`">
        <span class="task-col__dot"></span>
        <span class="task-col__title">{{ $t('taskStatuses.' + col.status) }}</span>
        <span class="task-col__count">{{ col.items.length }}</span>
      </div>

      <draggable
        v-model="col.items"
        :group="{ name: 'tasks' }"
        :animation="180"
        ghost-class="task-card--ghost"
        drag-class="task-card--drag"
        class="task-col__list"
        @change="evt => onChange(evt, col.status)"
      >
        <div
          v-for="task in col.items"
          :key="task.id"
          class="task-card"
          :style="{ '--accent': priorityColor(task.priority) }"
          @click="$emit('open', task)"
        >
          <div class="task-card__top">
            <span class="task-card__type" :style="{ background: meta(task.type).color }">
              <i :class="meta(task.type).icon"></i>
            </span>
            <span class="task-card__priority" :class="'text-' + priorityVariant(task.priority)">
              {{ $t('taskPriorities.' + task.priority) }}
            </span>
          </div>
          <div class="task-card__title">{{ task.title }}</div>
          <div class="task-card__type-label">{{ $t('taskTypes.' + task.type) }}</div>
          <div class="task-card__foot">
            <span class="task-card__assignee" v-if="task.assigneeName">
              <b-avatar size="22" variant="light-primary" :text="initials(task.assigneeName)"/>
              <span class="ml-50">{{ task.assigneeName }}</span>
            </span>
            <span class="task-card__assignee text-muted" v-else>
              <i class="fa-regular fa-user mr-25"></i>{{ $t('tasks.unassigned') }}
            </span>
            <span v-if="task.dueDate" class="task-card__due" :class="{ 'is-over': isOverdue(task) }">
              <i class="fa-regular fa-clock mr-25"></i>{{ task.dueDate | momentDate }}
            </span>
          </div>
        </div>

        <div slot="footer" v-if="col.items.length === 0" key="empty" class="task-col__empty">
          {{ $t('tasks.noTasksHere') }}
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { taskMeta, BOARD_STATUSES, PRIORITY_VARIANT, PRIORITY_COLOR } from '@/model/task/taskTypeMeta'

export default {
  name: 'TaskBoard',
  components: { draggable },
  props: {
    tasks: { type: Array, default: () => [] },
  },
  data: () => ({
    columns: BOARD_STATUSES.map(s => ({ status: s, items: [] })),
  }),
  watch: {
    tasks: { immediate: true, handler() { this.sync() } },
  },
  methods: {
    meta(type) { return taskMeta(type) },
    priorityVariant(p) { return (PRIORITY_VARIANT[p] || 'light-secondary').replace('light-', '') },
    priorityColor(p) { return PRIORITY_COLOR[p] || '#b8c2cc' },
    initials(name) {
      return (name || '').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase()
    },
    isOverdue(task) {
      return !!task.dueDate && task.status !== 'DONE' && this.$moment(task.dueDate).isBefore(this.$moment())
    },
    sync() {
      this.columns.forEach(col => {
        col.items = (this.tasks || []).filter(t => t.status === col.status)
      })
    },
    onChange(evt, status) {
      if (evt && evt.added && evt.added.element) {
        this.$emit('status-change', { id: evt.added.element.id, status })
      }
    },
  },
}
</script>

<style scoped lang="scss">
.task-board {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  overflow-x: auto;
  padding-bottom: 0.5rem;
}
.task-col {
  flex: 1 1 0;
  min-width: 270px;
  background: #f7f8fc;
  border-radius: 14px;
  padding: 0.6rem;
  display: flex;
  flex-direction: column;
  max-height: 72vh;
}
.task-col__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem 0.7rem;
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
}
.task-col__dot { width: 9px; height: 9px; border-radius: 50%; background: #b8c2cc; }
.task-col__head--new .task-col__dot { background: #00cfe8; }
.task-col__head--in_progress .task-col__dot { background: #ff9f43; }
.task-col__head--done .task-col__dot { background: #28c76f; }
.task-col__title { flex: 1; }
.task-col__count {
  background: rgba(110, 117, 159, 0.14); color: #6e6b7b;
  border-radius: 8px; padding: 1px 8px; font-size: 0.72rem;
}
.task-col__list { flex: 1; overflow-y: auto; min-height: 40px; }
.task-col__empty {
  text-align: center; color: #b8c2cc; font-size: 0.8rem;
  padding: 1.2rem 0; border: 1px dashed rgba(110, 117, 159, 0.25); border-radius: 10px;
}

.task-card {
  position: relative;
  background: #fff;
  border-radius: 12px;
  padding: 0.7rem 0.8rem 0.7rem 0.95rem;
  margin-bottom: 0.6rem;
  box-shadow: 0 4px 12px -8px rgba(20, 24, 50, 0.3);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  border: 1px solid #eef0f5;
  &::before {
    content: ''; position: absolute; left: 0; top: 8px; bottom: 8px; width: 3px;
    border-radius: 3px; background: var(--accent);
  }
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 22px -10px rgba(20, 24, 50, 0.4); }
}
.task-card--ghost { opacity: 0.5; }
.task-card--drag { box-shadow: 0 16px 34px -10px rgba(20, 24, 50, 0.5); }
.task-card__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.4rem; }
.task-card__type {
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 8px; color: #fff; font-size: 0.78rem;
}
.task-card__priority { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; }
.task-card__title { font-weight: 600; font-size: 0.88rem; color: #2f2b50; line-height: 1.25; }
.task-card__type-label { font-size: 0.72rem; color: #a0a4b8; margin-top: 0.1rem; }
.task-card__foot {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 0.6rem; font-size: 0.72rem; gap: 0.4rem;
}
.task-card__assignee { display: inline-flex; align-items: center; color: #6e6b7b; min-width: 0; }
.task-card__assignee span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 110px; }
.task-card__due { color: #6e6b7b; white-space: nowrap; }
.task-card__due.is-over { color: #ea5455; font-weight: 600; }

body.dark-layout {
  .task-col { background: var(--crm-dark-surface-2, #2e3850); }
  .task-card { background: var(--crm-dark-surface, #232c3e); border-color: rgba(255,255,255,0.06); }
  .task-card__title { color: #d0d2d6; }
  .task-col__count { background: rgba(255,255,255,0.08); color: #b4b7bd; }
}
</style>
